// @ts-nocheck
/**
 * applyLayoutSvgPaint — rewrite fill / stroke / stroke-width on layout decorative SVGs.
 *
 * Used by host SvgNode and editor preview so paint stays consistent across platforms.
 * Skips `none` and `url(...)` paints so gradients and intentional hollow shapes are preserved.
 * Replaces `currentColor` and other concrete paint values.
 */

export type LayoutSvgPaint = {
  fill?: string | null;
  stroke?: string | null;
  strokeWidth?: number | null;
};

const SKIP_PAINT_VALUE = /^(none|url\s*\()/i;
const QUOTED_VALUE = `(["'])([^"']*)\\1`;

function isReplaceablePaintValue(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) return false;
  if (trimmed.toLowerCase() === 'currentcolor') return true;
  return !SKIP_PAINT_VALUE.test(trimmed);
}

function replacePresentationAttr(xml: string, attrName: string, nextValue: string): string {
  const pattern = new RegExp(`\\b${attrName}\\s*=\\s*${QUOTED_VALUE}`, 'gi');
  return xml.replace(pattern, (full, _quote: string, current: string) => {
    if (!isReplaceablePaintValue(current)) {
      return full;
    }
    return `${attrName}="${nextValue}"`;
  });
}

function replaceStyleDeclarations(xml: string, cssProp: string, nextValue: string): string {
  const stylePattern = new RegExp(`\\bstyle\\s*=\\s*(["'])([^"']*)\\1`, 'gi');
  return xml.replace(stylePattern, (full, quote: string, styleBody: string) => {
    const declPattern = new RegExp(`(^|;)\\s*${cssProp}\\s*:\\s*([^;]*)`, 'gi');
    let changed = false;
    const nextBody = styleBody.replace(declPattern, (declFull, prefix: string, current: string) => {
      if (!isReplaceablePaintValue(current)) {
        return declFull;
      }
      changed = true;
      return `${prefix}${cssProp}:${nextValue}`;
    });
    if (!changed) return full;
    return `style=${quote}${nextBody}${quote}`;
  });
}

/**
 * Apply optional fill / stroke / strokeWidth to SVG markup.
 * No-ops when all paint fields are null/undefined.
 */
export function applyLayoutSvgPaint(xml: string, paint: LayoutSvgPaint): string {
  if (!xml.includes('<')) {
    return xml;
  }

  const fill = typeof paint.fill === 'string' && paint.fill.trim() !== '' ? paint.fill.trim() : null;
  const stroke =
    typeof paint.stroke === 'string' && paint.stroke.trim() !== '' ? paint.stroke.trim() : null;
  const strokeWidth =
    typeof paint.strokeWidth === 'number' && Number.isFinite(paint.strokeWidth) && paint.strokeWidth >= 0
      ? paint.strokeWidth
      : null;

  if (fill == null && stroke == null && strokeWidth == null) {
    return xml;
  }

  let result = xml;

  if (fill != null) {
    result = replacePresentationAttr(result, 'fill', fill);
    result = replaceStyleDeclarations(result, 'fill', fill);
    // CSS `color` drives remaining currentColor inheritance (e.g. stroke-only icons).
    result = replacePresentationAttr(result, 'color', fill);
    result = replaceStyleDeclarations(result, 'color', fill);
  }

  if (stroke != null) {
    result = replacePresentationAttr(result, 'stroke', stroke);
    result = replaceStyleDeclarations(result, 'stroke', stroke);
  }

  if (strokeWidth != null) {
    const sw = String(strokeWidth);
    result = replacePresentationAttr(result, 'stroke-width', sw);
    result = replaceStyleDeclarations(result, 'stroke-width', sw);
    result = replaceStyleDeclarations(result, 'strokeWidth', sw);
  }

  return result;
}
